import { forwardRef, useMemo } from "react";
import { BodyView, ITEM_TYPES, TabKeys, TemplateType } from "../../../../types";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { ImageVideoPreview } from "../../../common/ImageVideoPreview";

interface BackgroundProps {
  handleTimeUpdate: () => void;
  handleLoadedMetadata: () => void;
}

export const BackgroundPreviewDynamic = forwardRef<
  HTMLVideoElement,
  BackgroundProps
>(({ handleTimeUpdate, handleLoadedMetadata }, ref) => {
  const {
    background,
    transition,
    intro,
    closing,
    setCurrentItem,
    currentChapter,
  } = useTemplateEditorStore((store) => ({
    background: store.templateState.background,
    transition: store.templateState.transition,
    intro: store.templateState.intro,
    closing: store.templateState.closing,
    setCurrentItem: store.setCurrentItem,
    currentChapter: store.currentChapter,
  }));

  const { setActiveTab } = useTemplatesStatusStore((state) => ({
    setActiveTab: state.setActiveTab,
  }));

  const backgroundItem:
    | {
        view: BodyView;
        assetId?: string;
        assetPath?: string;
      }
    | undefined = useMemo(() => {
    if (currentChapter.type === TemplateType.CHAPTER) {
      if (background.bodyView === "Video") {
        return {
          view: "Video",
          assetId: background.bodyVideoAsset,
          assetPath: background.bodyVideo,
        };
      } else if (background.bodyView === "Picture") {
        return {
          view: "Picture",
          assetId: background.bodyImageAsset,
          assetPath: background.bodyImage,
        };
      } else {
        return undefined;
      }
    }

    if (currentChapter.type === TemplateType.INTRODUCTION && intro.enable) {
      return {
        view: "Video",
        assetId: intro.assetId,
        assetPath: intro.assetPath,
      };
    }

    if (currentChapter.type === TemplateType.CLOSING && closing.enable) {
      return {
        view: "Video",
        assetId: closing.assetId,
        assetPath: closing.assetPath,
      };
    }

    if (
      currentChapter.type === TemplateType.TRANSITION &&
      transition.view !== "Color"
    ) {
      return {
        view: transition.view,
        assetId: transition.assetId,
        assetPath: transition.assetPath,
      };
    }

    return undefined;
  }, [currentChapter.type, background, transition, intro, closing]);

  if (!backgroundItem) return null;

  return (
    <ImageVideoPreview
      ref={ref}
      type={
        backgroundItem.view === "Video" ? ITEM_TYPES.Video : ITEM_TYPES.Image
      }
      assetId={backgroundItem.assetId}
      path={backgroundItem.assetPath}
      videoProps={{
        onTimeUpdate: handleTimeUpdate,
        onLoadedMetadata: handleLoadedMetadata,
      }}
      style={{
        pointerEvents: "none",
      }}
      onClick={() => {
        setCurrentItem({ type: ITEM_TYPES.Background });
        setActiveTab(TabKeys.objects);
      }}
    />
  );
});

BackgroundPreviewDynamic.displayName = "BackgroundPreviewDynamic";
